import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import '../styles/analytics.css';

type Period = '1d' | '7d';

interface AnalyticsData {
  period: string;
  totalVisits: number;
  totalRequests: number;
  totalBandwidth: number;
  timeseries: Array<{ date: string; visits: number; requests: number; bandwidth: number }>;
  topPages: Array<{ path: string; requests: number }>;
  topCountries: Array<{ country: string; requests: number }>;
}

function formatBandwidth(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export default function Analytics() {
  const { siteId } = useParams<{ siteId: string }>();
  const [period, setPeriod] = useState<Period>('7d');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getAnalytics(siteId, period);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [siteId, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Analytics</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Analytics</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  const isEmpty = !data || (data.totalRequests === 0 && data.timeseries.length === 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Analytics</h1>
      </div>

      {/* Period Selector */}
      <div className="analytics-period-bar">
        {(['1d', '7d'] as Period[]).map((p) => (
          <button
            key={p}
            className={`analytics-period-btn ${period === p ? 'active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p === '1d' ? 'Today' : '7 Days'}
          </button>
        ))}
      </div>

      {isEmpty ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></div>
          <h2>No traffic data yet</h2>
          <p>Share your site URL to start getting visitors!</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="analytics-summary">
            <div className="analytics-card">
              <div className="analytics-card-icon"><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg></div>
              <div className="analytics-card-content">
                <div className="analytics-card-value">{formatNumber(data!.totalVisits)}</div>
                <div className="analytics-card-label">Visitors</div>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-icon"><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>
              <div className="analytics-card-content">
                <div className="analytics-card-value">{formatNumber(data!.totalRequests)}</div>
                <div className="analytics-card-label">Page Views</div>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-icon"><svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h.01"/><path d="M7 20v-4"/><path d="M12 20v-8"/><path d="M17 20V8"/><path d="M22 4v16"/></svg></div>
              <div className="analytics-card-content">
                <div className="analytics-card-value">{formatBandwidth(data!.totalBandwidth)}</div>
                <div className="analytics-card-label">Bandwidth</div>
              </div>
            </div>
          </div>

          {/* Traffic Chart */}
          {data!.timeseries.length > 0 && (
            <div className="analytics-chart-section">
              <h2 className="analytics-section-title">Traffic</h2>
              <div className="analytics-chart">
                <div className="analytics-chart-bars">
                  {(() => {
                    const maxVisits = Math.max(...data!.timeseries.map(d => d.visits), 1);
                    const barCount = data!.timeseries.length;
                    // Show a label every N bars depending on period length
                    const labelInterval = barCount <= 7 ? 1 : barCount <= 30 ? 5 : 10;

                    return data!.timeseries.map((day, i) => (
                      <div key={day.date} className="analytics-bar-wrapper">
                        <div
                          className="analytics-bar"
                          style={{ height: `${Math.max((day.visits / maxVisits) * 100, 2)}%` }}
                          title={`${formatDateLabel(day.date)}: ${day.visits} visitors, ${day.requests} views`}
                        />
                        {i % labelInterval === 0 && (
                          <span className="analytics-bar-label">
                            {formatDateLabel(day.date)}
                          </span>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Bottom Tables */}
          <div className="analytics-tables">
            {/* Top Pages */}
            {data!.topPages.length > 0 && (
              <div className="analytics-table-section">
                <h2 className="analytics-section-title">Top Pages</h2>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th className="analytics-col-number">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.topPages.map((page, i) => (
                      <tr key={i}>
                        <td className="analytics-cell-path">{page.path}</td>
                        <td className="analytics-col-number">{formatNumber(page.requests)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Top Countries */}
            {data!.topCountries.length > 0 && (
              <div className="analytics-table-section">
                <h2 className="analytics-section-title">Top Countries</h2>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th className="analytics-col-number">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.topCountries.map((country, i) => (
                      <tr key={i}>
                        <td>{country.country}</td>
                        <td className="analytics-col-number">{formatNumber(country.requests)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
